/**
 * ArticleListPageTemplate - TRANSFORMER PATTERN para páginas de listagem de artigos
 *
 * Template compartilhado entre TODAS as páginas que listam artigos:
 * - ArticlePage (todos os artigos)
 * - ArticlesByTagPage (artigos filtrados por tag)
 * - Favorites (artigos favoritos)
 * - [Futuras páginas]
 *
 * TRANSFORMAÇÃO via props:
 * - baseFilter: Aplica filtro base na busca de artigos (ex: {tagId: '123'}, {isFavorite: true})
 * - pageTitle: Título da página
 * - pageConfig: Configurações opcionais (showScraping, showTagHeader, etc)
 *
 * Adicionar funcionalidade: Modificar APENAS este arquivo = funciona em TODAS as páginas!
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Button, Divider, useMediaQuery } from '@mui/material';
// Ajustando caminhos de importação
import ArticlePageTemplate from '../ArticlePageTemplate/ArticlePageTemplate';
import ArticleMeasurementTable from '../../organisms/ArticleMeasurementTable/ArticleMeasurementTable';
// KeyboardShortcutsHelp removido - atalhos continuam funcionando via useKeyboardShortcuts
import { exportArticlesToExcel, exportSelectedArticles } from '../../../utils/excelExport';

// Importando hooks existentes
import useArticleFetcherWithCache from '../../../hooks/data/useArticleFetcherWithCache';
import { buildArticleQueryParams } from '../../../hooks/data/useArticlesQuery';
import useAdvancedSelection from '../../../hooks/utils/useAdvancedSelection';
import api, { bulkAnalyzeKpia, bulkNativeReviewArticles } from '../../../services/api.js';
import useArticleCRUD from '../../../hooks/operations/useArticleCRUD';
import useArticleTags from '../../../hooks/operations/useArticleTags';
import useArticleBulkActions from '../../../hooks/operations/useArticleBulkActions';
import useSentimentPropagation from '../../../hooks/operations/useSentimentPropagation';
import { mergeTags, fetchCurrentArticleTags, hydrateArticleTags } from '../../../utils/tagUtils';
import { useTheme } from '@mui/material/styles';
import useKeyboardShortcuts from '../../../hooks/useKeyboardShortcuts';
import { useMIA } from '../../../context/MIAContext';
import { useBrand } from '../../../context/BrandContext';
import useArticleListPageUiState from './useArticleListPageUiState';
import useArticleListFilters from './useArticleListFilters';
import ArticleListEmptyState from './ArticleListEmptyState';
import ArticleListPageAlerts from './ArticleListPageAlerts';
import ArticleListDialogs from './ArticleListDialogs';
import {
  collectionOptions,
  contentTypeOptions,
  dateOptions,
  exposureOptions,
  mediaTypeOptions,
  sentimentOptions,
  sortOptions,
  statusOptions,
  tierOptions
} from './articleListFilterOptions';
import useArticleListDerivedData from './useArticleListDerivedData';

const ArticleList = React.lazy(() => import('../../organisms/ArticleList/ArticleList'));

/**
 * Props do ArticleListPageTemplate
 */
const ArticleListPageTemplate = ({
  // Filtro base aplicado à busca de artigos
  baseFilter = {},

  // Configuração da página
  pageTitle = 'Artigos',
  pageSubtitle = '',

  // Configurações opcionais
  pageConfig = {
    showScraping: true,           // Mostrar botão de scraping (padrão: true para ArticlePage)
    showBackgroundScraping: true,  // Mostrar modal de background scraping
    showTagHeader: false,          // Mostrar header da tag (para ArticlesByTagPage)
    showCollectionHeader: false,   // Mostrar header da coleção (futuro)
  }
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Hook para buscar e manipular artigos - ÚNICO LUGAR onde baseFilter é usado!
  // Desestrutura baseFilter para extrair tagId, collectionId e outros filtros
  const { tagId, collectionId, ...otherFilters } = baseFilter;

  // Tipo de data controlado no template
  const [quickDateType, setQuickDateType] = React.useState(
    () => localStorage.getItem('article_quickDateType') || 'created'
  );

  // Previne congelamento da interface no roteamento
  const [isMounting, setIsMounting] = React.useState(true);
  React.useEffect(() => {
    // Libera a renderização da lista pesada apenas após a tela já ter feito a transição
    setIsMounting(false);
  }, []);

  // Estado para informações contextuais (coleção ou tag)
  const [contextInfo, setContextInfo] = React.useState(null);
  const [, setContextLoading] = React.useState(false);

  // Buscar informações da coleção ou tag quando necessário
  // Adiciona 'articles' na dependência para re-buscar quando artigos mudarem (add/remove tag)
  React.useEffect(() => {
    const fetchContextInfo = async () => {
      if (collectionId && pageConfig.showCollectionHeader) {
        try {
          setContextLoading(true);
          const res = await api.get(`/collections/${collectionId}`);
          setContextInfo({
            type: 'collection',
            ...res.data.data
          });
        } catch (err) {
          console.error('Erro ao buscar coleção:', err);
        } finally {
          setContextLoading(false);
        }
      } else if (tagId && pageConfig.showTagHeader) {
        try {
          setContextLoading(true);
          const res = await api.get(`/tags/${tagId}`);
          setContextInfo({
            type: 'tag',
            ...res.data.data
          });
        } catch (err) {
          console.error('Erro ao buscar tag:', err);
        } finally {
          setContextLoading(false);
        }
      } else {
        setContextInfo(null);
      }
    };

    fetchContextInfo();
  }, [collectionId, tagId, pageConfig.showCollectionHeader, pageConfig.showTagHeader]);

  // Busca as informações dos artigos
  const {
    articles,
    setArticles,
    loading,
    error,
    totalCount,
    timePeriod,
    setTimePeriod,
    filters,
    handlePageChange,
    handleFilterChange,
    handleToggleFavorite,
    handleToggleRead,
    refreshArticles,
    sortBy,
    setSortBy,
    tags,
    refreshTags,
    allSourceOptions,
    hasMore,
    loadMore,
    forceRefresh,
    isRestoringSession,
    hasNewArticles,
    refreshCacheAndData,
    removeArticlesFromCache,
    updateArticleInState
  } = useArticleFetcherWithCache({
    tagId,
    collectionId,
    initialFilters: { ...otherFilters, dateType: quickDateType },
    pageSize: isMobile ? 5 : 60,
  });

  // Título dinâmico baseado no contexto
  const displayTitle = contextInfo?.name || pageTitle;


  // Listener para evento de atualização de tags de coleção
  // Força re-fetch quando tags são adicionadas/removidas de uma coleção
  React.useEffect(() => {
    if (!collectionId) return;

    const handleCollectionTagsUpdated = (event) => {
      if (event.detail?.collectionId === collectionId) {
        console.log('[ArticleListPageTemplate] Collection tags updated, forcing article refresh');
        forceRefresh();
      }
    };

    window.addEventListener('collectionTagsUpdated', handleCollectionTagsUpdated);
    return () => window.removeEventListener('collectionTagsUpdated', handleCollectionTagsUpdated);
  }, [collectionId, forceRefresh]);

  // Estado local de UI da pagina
  const {
    showEmpty,
    selectedArticle,
    setSelectedArticle,
    modalOpen,
    setModalOpen,
    variant,
    setVariant,
    searchQuery,
    setSearchQuery,
    searchDebounceRef,
    snackbar,
    setSnackbar,
    selectedSources,
    setSelectedSources,
    selectedSentiments,
    setSelectedSentiments,
    selectedExposures,
    setSelectedExposures,
    selectedTags,
    setSelectedTags,
    selectedCollections,
    setSelectedCollections,
    selectedMediaTypes,
    setSelectedMediaTypes,
    quickNewsletterOpen,
    setQuickNewsletterOpen,
    quickNewsletterArticles,
    setQuickNewsletterArticles,
    selectedContentTypes,
    setSelectedContentTypes,
    selectedTiers,
    setSelectedTiers,
    selectedStatuses,
    setSelectedStatuses,
    selectedEntityType,
    setSelectedEntityType,
    showFavoritesOnly,
    setShowFavoritesOnly,
    filtersOpen,
    setFiltersOpen,
    activeFiltersExpanded,
    setActiveFiltersExpanded,
    editModalOpen,
    setEditModalOpen,
    tagDialogOpen,
    setTagDialogOpen,
    articleForTags,
    setArticleForTags,
    confirmDialog,
    setConfirmDialog,
    smartScraperLoading,
    setSmartScraperLoading,
    aiBulkLoading,
    setAiBulkLoading,
    bulkTagDialogOpen,
    setBulkTagDialogOpen,
    bulkEditDialogOpen,
    setBulkEditDialogOpen,
    sentimentDialogOpen,
    setSentimentDialogOpen,
    bulkSentiment,
    setBulkSentiment,
    scrapingModalOpen,
    setScrapingModalOpen,
    collectionDialogOpen,
    setCollectionDialogOpen,
    articlesToAddToCollection,
    setArticlesToAddToCollection,
    removeTagsDialogOpen,
    setRemoveTagsDialogOpen,
    removeFromCollectionsDialogOpen,
    setRemoveFromCollectionsDialogOpen,
    tagPropagationDialogOpen,
    setTagPropagationDialogOpen,
    tagPropagationLoading,
    setTagPropagationLoading,
    pendingTagsToPropagate,
    setPendingTagsToPropagate,
    setPendingArticleIdForTags,
    similarArticlesForTags,
    setSimilarArticlesForTags
  } = useArticleListPageUiState({
    loading,
    articlesCount: articles.length,
    filters,
    isMobile
  });

  // Wrapper para handleToggleFavorite 
  // O filtro filteredArticles já cuida de remover artigos desfavoritados da página de Favoritos
  const customToggleFavorite = React.useCallback(async (articleId) => {
    // Chama o toggle favorite original (com optimistic update do React Query)
    await handleToggleFavorite(articleId);
    // O useMemo filteredArticles vai automaticamente remover da lista se showFavoritesOnly estiver ativo
  }, [handleToggleFavorite]);

  const navigate = useNavigate();

  // Gestão de Pendências de Impresso 
  const { activeBrandData } = useBrand();
  const [pendingPrintsCount, setPendingPrintsCount] = React.useState(0);
  const [pendingPrintsModalOpen, setPendingPrintsModalOpen] = React.useState(false);

  React.useEffect(() => {
    const fetchPendingPrints = async () => {
      if (!activeBrandData?._id) return;
      try {
        const res = await api.get(`/prints/pending/${activeBrandData._id}`);
        setPendingPrintsCount(res.data.data?.length || 0);
      } catch (err) {
        console.warn('Erro ao verificar impressos pendentes', err);
      }
    };
    fetchPendingPrints();
  }, [activeBrandData]);

  // Atalhos de teclado
  useKeyboardShortcuts({
    'ctrl+a': (e) => {
      if (selectionMode) {
        selectAll();
      }
    },
    'escape': () => {
      if (selectionMode) {
        toggleSelectionMode();
      } else if (filtersOpen) {
        setFiltersOpen(false);
      }
    },
    'delete': () => {
      if (selectionMode && selectedArticles.length > 0) {
        handleDeleteBulk();
      }
    }
  }, true);
  // Preferência de propagação de tags (localStorage)
  const TAG_PROPAGATION_PREF_KEY = 'tagPropagation_preference';
  const getTagPropagationPreference = () => {
    try {
      return localStorage.getItem(TAG_PROPAGATION_PREF_KEY) || 'ask';
    } catch {
      return 'ask';
    }
  };
  const setTagPropagationPreference = (pref) => {
    try {
      localStorage.setItem(TAG_PROPAGATION_PREF_KEY, pref);
    } catch (e) {
      console.error('Erro ao salvar preferência:', e);
    }
  };

  // Hook do MIA para verificar se funcionalidades de IA estão ativas
  const { isSentimentAIEnabled } = useMIA();

  const {
    availableTags,
    entityTypeOptions,
    filteredArticles,
    hasEmptyStateFilters
  } = useArticleListDerivedData({
    articles,
    selectedStatuses,
    showFavoritesOnly,
    isFavoritePage: otherFilters.isFavorite === true,
    timePeriod,
    selectedSources,
    selectedSentiments,
    selectedTags,
    selectedCollections,
    selectedMediaTypes,
    selectedContentTypes,
    selectedTiers,
    selectedEntityType,
    searchQuery,
    activeBrandName: activeBrandData?.name
  });

  const handleArticleUpdated = React.useCallback((updatedArticle) => {
    const articlePayload = updatedArticle?.data || updatedArticle?.article || updatedArticle;
    if (!articlePayload?._id) return;

    const hydratedArticle = hydrateArticleTags(articlePayload, [
      ...(availableTags || []),
      ...(tags || [])
    ]);

    updateArticleInState(hydratedArticle._id, article => ({
      ...article,
      ...hydratedArticle
    }));

    setSelectedArticle(prev => (
      prev?._id === hydratedArticle._id
        ? { ...prev, ...hydratedArticle }
        : prev
    ));
  }, [availableTags, tags, updateArticleInState, setSelectedArticle]);

  // Hook para gerenciar seleção de artigos
  const {
    selectionMode,
    selectedItems: selectedArticles,
    toggleSelectionMode,
    toggleItemSelection,
    selectAll,
    clearSelection,
    cleanupSelection,
    setSelectedItems
  } = useAdvancedSelection(filteredArticles);

  // Hooks especializados para operações
  const { deleteArticle } = useArticleCRUD(setArticles, (msg, sev) => setSnackbar({ open: true, message: msg, severity: sev || 'success' }), cleanupSelection);
  const { addTags, removeTag } = useArticleTags(setArticles, (msg, sev) => setSnackbar({ open: true, message: msg, severity: sev || 'success' }), tagId);
  const {
    toggleFavoriteBulk,
    toggleReadBulk,
    updateSentimentBulk,
    analyzeSentimentBulk,
    addTagsBulk,
    updateFieldsBulk,
    deleteArticlesBulk,
    bulkActionLoading,
    bulkActionProgress
  } = useArticleBulkActions(setArticles, (msg, sev) => setSnackbar({ open: true, message: msg, severity: sev || 'success' }), cleanupSelection);

  // Hook para propagação de sentimento com confirmação
  const {
    dialogOpen: propagationDialogOpen,
    dialogLoading: propagationDialogLoading,
    pendingSentiment,
    similarArticles,
    handleDialogConfirm: handlePropagationConfirm,
    handleDialogCancel: handlePropagationCancel,
    handleDialogClose: handlePropagationClose,
    updateSentiment: updateSentimentWithConfirmation
  } = useSentimentPropagation(setArticles, (msg, sev) => setSnackbar({ open: true, message: msg, severity: sev || 'success' }));

  const handleSortChange = (value) => {
    setSortBy(value);
    localStorage.setItem('article_sortBy', value);
  };
  const handleArticleClick = (article, event) => {
    // Se Ctrl+Click, apenas ativa modo seleção (não seleciona o card)
    if (event && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      event.stopPropagation();
      if (!selectionMode) {
        toggleSelectionMode();
      }
      return;
    }

    // Comportamento normal: abrir modal
    setSelectedArticle(article);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedArticle(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleBackgroundScrapingJobStarted = React.useCallback((jobData) => {
    if (jobData.status === 'completed') {
      refreshCacheAndData();
      setSnackbar({
        open: true,
        message: `${jobData.successCount} artigos foram adicionados!`,
        severity: 'success'
      });
    }
  }, [refreshCacheAndData, setSnackbar]);

  const handleQuickNewsletterEmailSent = React.useCallback(() => {
    setSnackbar({ open: true, message: 'Newsletter enviada com sucesso!', severity: 'success' });
  }, [setSnackbar]);

  const handleCardsPerPageChange = (value) => {
    localStorage.setItem('article_cardsPerPage', value.toString());
    if (typeof handlePageChange === 'function') {
      handlePageChange(null, 1);
    }
  };

  const handleToggleReadArticle = async (articleId) => {
    await handleToggleRead(articleId);
    if (selectedArticle && selectedArticle._id === articleId) {
      setSelectedArticle({
        ...selectedArticle,
        isRead: !selectedArticle.isRead
      });
    }
  };

  const handleVariantChange = (newVariant) => {
    setVariant(newVariant);
    localStorage.setItem('article_variant', newVariant);
  };

  // Handler padronizado para edição
  const onEdit = (article) => {
    console.log('🚀 ArticlePage - onEdit chamado:', {
      articleId: article?._id,
      articleTitle: article?.title,
      isNewArticle: !article
    });
    setSelectedArticle(article); // null para novo artigo
    setEditModalOpen(true);
    console.log('🚀 ArticlePage - editModalOpen definido como true');
  };

  const handleCloseEditModal = () => {
    console.log('🚪 ArticlePage - handleCloseEditModal chamado');
    setEditModalOpen(false);
    setSelectedArticle(null);
    console.log('🚪 ArticlePage - modal fechado');
  };

  const handleSaveArticle = async (updatedArticle) => {
    try {
      // Verificar se o artigo já foi atualizado via upload (não fazer chamada extra)
      // Se o artigo tem fileInfo, significa que veio de upload e já está atualizado
      const cameFromUpload = updatedArticle.fileInfo;

      if (!cameFromUpload) {
        // Só fazer PUT se NÃO veio de upload (edição normal)
        const response = await api.put(`/articles/${updatedArticle._id}`, updatedArticle);
        
        // Atualiza a lista local imediatamente para refletir no card sem precisar recarregar a página
        const savedArticle = response.data?.data || response.data?.article || response.data || updatedArticle;
        setArticles(prev => prev.map(a => a._id === savedArticle._id ? savedArticle : a));
      }



      // Para uploads, abrir o modal do artigo com o artigo atualizado
      if (cameFromUpload) {
        try {
          // Buscar o artigo atualizado diretamente da API
          const response = await api.get(`/articles/${updatedArticle._id}`);
          const refreshedArticle = response.data.article || response.data;
          setSelectedArticle(refreshedArticle);
          setModalOpen(true);
        } catch (err) {
          console.error('Erro ao buscar artigo atualizado:', err);
          // Fallback: tentar encontrar na lista atual
          const fallbackArticle = articles.find(a => a._id === updatedArticle._id);
          if (fallbackArticle) {
            setSelectedArticle(fallbackArticle);
            setModalOpen(true);
          }
        }
      }

      setEditModalOpen(false);

      // Só limpar selectedArticle se NÃO veio de upload
      if (!cameFromUpload) {
        setSelectedArticle(null);
      }

      setSnackbar({
        open: true,
        message: cameFromUpload ? 'Vídeo enviado com sucesso' : 'Artigo atualizado com sucesso',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({ open: true, message: 'Erro ao atualizar artigo', severity: 'error' });
    }
  };

  // Determina a lista de navegação: se em modo seleção com artigos selecionados, navega apenas entre eles
  const navigationList = (selectionMode && selectedArticles.length > 0)
    ? articles.filter(a => selectedArticles.includes(a._id))
    : articles;

  const mergeMeasurementArticleUpdate = (article, update) => {
    if (!update) return article;

    const merged = { ...article, ...update };
    const metric = {
      ...(article?.onlineCentimetragem || article?.editorialOccupancy || {}),
      ...(update?.onlineCentimetragem || update?.editorialOccupancy || {})
    };
    let hasMetricUpdate = Boolean(update?.onlineCentimetragem || update?.editorialOccupancy);

    Object.entries(update).forEach(([key, value]) => {
      if (!key.startsWith('onlineCentimetragem.')) return;

      const path = key.replace('onlineCentimetragem.', '').split('.');
      let current = metric;
      path.slice(0, -1).forEach((part) => {
        if (!current[part] || typeof current[part] !== 'object') current[part] = {};
        current = current[part];
      });
      current[path[path.length - 1]] = value;
      delete merged[key];
      hasMetricUpdate = true;
    });

    if (hasMetricUpdate) {
      merged.editorialOccupancy = metric;
      merged.onlineCentimetragem = metric;
    }

    return merged;
  };

  const handleMeasurementSave = async (articleId, patch) => {
    try {
      const response = await api.put(`/articles/${articleId}`, patch);
      const savedArticle = response.data?.data || response.data?.article || response.data;

      setArticles(prev => prev.map(article => (
        article._id === articleId
          ? mergeMeasurementArticleUpdate(article, savedArticle || patch)
          : article
      )));

      setSelectedArticle(prev => (
        prev?._id === articleId
          ? mergeMeasurementArticleUpdate(prev, savedArticle || patch)
          : prev
      ));

      setSnackbar({
        open: true,
        message: 'Campos de mensuracao atualizados',
        severity: 'success'
      });

      return savedArticle || patch;
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Erro ao atualizar campos de mensuracao',
        severity: 'error'
      });
      throw err;
    }
  };

  const handleMeasurementBulkSave = async (articleIds, patch) => {
    const uniqueIds = Array.from(new Set(articleIds || [])).filter(Boolean);

    if (uniqueIds.length === 0) {
      return null;
    }

    try {
      const responses = await Promise.all(
        uniqueIds.map(articleId => api.put(`/articles/${articleId}`, patch))
      );

      const savedById = new Map();
      responses.forEach((response, index) => {
        const savedArticle = response.data?.data || response.data?.article || response.data;
        savedById.set(uniqueIds[index], savedArticle || patch);
      });

      setArticles(prev => prev.map(article => {
        const savedArticle = savedById.get(article._id);
        return savedArticle ? mergeMeasurementArticleUpdate(article, savedArticle) : article;
      }));

      setSelectedArticle(prev => {
        if (!prev?._id || !savedById.has(prev._id)) return prev;
        return mergeMeasurementArticleUpdate(prev, savedById.get(prev._id));
      });

      setSnackbar({
        open: true,
        message: `${uniqueIds.length} artigo(s) atualizados na mensuracao`,
        severity: 'success'
      });

      return { updated: uniqueIds.length };
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Erro ao atualizar mensuracao em lote',
        severity: 'error'
      });
      throw err;
    }
  };

  const selectedIndex = navigationList.findIndex(a => a._id === (selectedArticle && selectedArticle._id));

  const handlePrevArticle = () => {
    if (selectedIndex > 0) {
      setSelectedArticle(navigationList[selectedIndex - 1]);
    }
  };

  const handleNextArticle = () => {
    if (selectedIndex < navigationList.length - 1) {
      setSelectedArticle(navigationList[selectedIndex + 1]);
    }
  };



  // Handler padronizado para remoção de tag de artigo
  const handleArticleRemoveTag = async (articleId, tagId) => {
    try {
      const id = typeof tagId === 'object' ? tagId._id : tagId;
      // useArticleTags já exibe o snackbar de sucesso e atualiza o estado
      await removeTag(articleId, id);
    } catch (err) {
      console.error('Erro ao remover tag na listagem:', err);
      // useArticleTags também exibe snackbar de erro, mas garantimos aqui caso falhe fora do hook
      if (!snackbar.open) {
        setSnackbar({
          open: true,
          message: err.message || 'Erro ao remover tag',
          severity: 'error'
        });
      }
    }
  };


  // Handlers de ações em massa conectados ao hook
  const {
    activeFilters,
    handleSearch,
    handleClearFilters,
    handleStatusesChange,
    handleToggleFavorites,
    handleRemoveFilter
  } = useArticleListFilters({
    filters,
    handleFilterChange,
    tagId,
    collectionId,
    setTimePeriod,
    searchQuery,
    setSearchQuery,
    searchDebounceRef,
    selectedSources,
    setSelectedSources,
    selectedSentiments,
    setSelectedSentiments,
    selectedExposures,
    setSelectedExposures,
    selectedTags,
    setSelectedTags,
    selectedCollections,
    setSelectedCollections,
    selectedMediaTypes,
    setSelectedMediaTypes,
    selectedContentTypes,
    setSelectedContentTypes,
    selectedTiers,
    setSelectedTiers,
    selectedStatuses,
    setSelectedStatuses,
    selectedEntityType,
    setSelectedEntityType,
    setShowFavoritesOnly,
    activeBrandData,
    dateOptions,
    tierOptions,
    statusOptions,
    exposureOptions
  });

  const handleFavorite = () => {
    toggleFavoriteBulk(selectedArticles);
  };

  // Estado para loading de seleção de todos
  const [selectingAll, setSelectingAll] = React.useState(false);

  // Handler para selecionar TODOS os artigos (incluindo os não carregados)
  const handleSelectAllPages = async () => {
    setSelectingAll(true);
    try {
      console.log('🔍 Buscando todos os IDs com filtros:', filters, 'timePeriod:', timePeriod, 'totalCount:', totalCount);

      // Fazer uma chamada à API para obter todos os IDs de artigos com os filtros atuais
      const queryParams = buildArticleQueryParams({
        page: 1,
        limit: totalCount || 1000,
        sort: sortBy,
        search: filters.search,
        sentiment: filters.sentiment,
        exposure: filters.exposure,
        isRead: filters.isRead,
        isFavorite: filters.isFavorite,
        tags: filters.tags,
        hasTags: filters.hasTags,
        sources: filters.siteName || filters.sources,
        mediaTypes: filters.mediaType || filters.mediaTypes,
        contentTypes: filters.contentType || filters.contentTypes,
        entityType: filters.entityType,
        timePeriod,
        startDate: filters.startDate,
        endDate: filters.endDate,
        dateType: filters.dateType,
        tagId,
        collectionId,
        tiers: filters.tier || filters.tiers,
        editorialOccupancyStatus: filters.editorialOccupancyStatus,
        qualitativeAnalysisStatus: filters.qualitativeAnalysisStatus
      });

      const response = await api.get(`/articles?${queryParams}&fields=_id`);

      console.log('📦 Resposta da API:', response.data);

      // A resposta pode estar em data.data ou data.articles dependendo da estrutura da API
      const articlesData = response.data.data || response.data.articles || response.data;
      const allIds = Array.isArray(articlesData)
        ? articlesData.map(article => article._id)
        : [];

      console.log('✅ IDs extraídos:', allIds.length, allIds);

      if (allIds.length === 0) {
        throw new Error('Nenhum ID foi retornado pela API');
      }

      setSelectedItems(allIds);

      setSnackbar({
        open: true,
        message: `${allIds.length} artigos selecionados`,
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ Erro ao selecionar todos os artigos:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao selecionar todos os artigos: ' + (error.message || 'Erro desconhecido'),
        severity: 'error'
      });
    } finally {
      setSelectingAll(false);
    }
  };

  // Handler para abrir o TagDialog em massa
  const handleAddTagBulk = () => {
    setBulkTagDialogOpen(true);
  };

  const handleEditFieldsBulk = () => {
    if (selectedArticles.length === 0) {
      setSnackbar({
        open: true,
        message: 'Selecione pelo menos um artigo',
        severity: 'warning'
      });
      return;
    }

    setBulkEditDialogOpen(true);
  };

  const handleApplyBulkFieldEdit = async (patch) => {
    const success = await updateFieldsBulk(selectedArticles, patch);

    if (success) {
      setBulkEditDialogOpen(false);
      refreshArticles();
    }
  };

  // Handler para adicionar tags em massa
  const handleBulkAddTags = async (selectedTags) => {
    // Separar tags novas (sem _id) das existentes
    const tagsToCreate = selectedTags.filter(tag => !tag._id);
    const existingTags = selectedTags.filter(tag => tag._id);
    let createdTags = [];

    if (tagsToCreate.length > 0) {
      const createPromises = tagsToCreate.map(tag => api.post('/tags', { name: tag.name }));
      const createdResponses = await Promise.all(createPromises);
      createdTags = createdResponses.map(res => res.data.data);
    }

    const allTagIds = [...existingTags, ...createdTags].map(tag => tag._id);
    await addTagsBulk(selectedArticles, allTagIds);

    // Atualizar lista de tags se novas tags foram criadas
    if (createdTags.length > 0) {
      // Aguardar 1 segundo para garantir persistência no banco
      await new Promise(resolve => setTimeout(resolve, 1000));
      refreshTags();
    }

    setBulkTagDialogOpen(false);
    setSnackbar({
      open: true,
      message: `Tags adicionadas a ${selectedArticles.length} artigos`,
      severity: 'success'
    });
  };

  // Handler para adicionar artigos em massa à coleção (toolbar de seleção)
  const handleAddToCollectionBulk = () => {
    if (selectedArticles.length === 0) {
      setSnackbar({
        open: true,
        message: 'Selecione pelo menos um artigo',
        severity: 'warning'
      });
      return;
    }
    setArticlesToAddToCollection(selectedArticles);
    setCollectionDialogOpen(true);
  };

  // Handler para adicionar um único artigo à coleção (menu do card)
  const handleAddToCollectionIndividual = (article) => {
    const articleId = typeof article === 'object' ? article._id : article;
    setArticlesToAddToCollection([articleId]);
    setCollectionDialogOpen(true);
  };

  const handleCollectionSuccess = ({ collectionsCount, articlesCount }) => {
    setSnackbar({
      open: true,
      message: `${articlesCount} artigo(s) adicionado(s) a ${collectionsCount} coleção(ões)`,
      severity: 'success'
    });
    setArticlesToAddToCollection([]);
    cleanupSelection();
  };

  // Handler para abrir o dialog de remover tags em massa
  const handleRemoveTagsBulk = () => {
    if (selectedArticles.length === 0) {
      setSnackbar({
        open: true,
        message: 'Selecione pelo menos um artigo',
        severity: 'warning'
      });
      return;
    }
    setRemoveTagsDialogOpen(true);
  };

  // Handler para sucesso na remoção de tags
  const handleRemoveTagsSuccess = (modifiedCount) => {
    // Recarregar artigos para refletir as mudanças
    refreshArticles();
    setSnackbar({
      open: true,
      message: `Tags removidas de ${modifiedCount} artigos`,
      severity: 'success'
    });
  };

  // Handler para abrir modal de remover de coleções em massa
  const handleRemoveFromCollectionsBulk = () => {
    if (selectedArticles.length === 0) {
      setSnackbar({
        open: true,
        message: 'Selecione pelo menos um artigo',
        severity: 'warning'
      });
      return;
    }
    setRemoveFromCollectionsDialogOpen(true);
  };

  // Handler para sucesso na remoção de coleções
  const handleRemoveFromCollectionsSuccess = (modifiedCount) => {
    // Recarregar artigos para refletir as mudanças
    refreshArticles();
    setSnackbar({
      open: true,
      message: `Artigos removidos de ${modifiedCount} coleções`,
      severity: 'success'
    });
  };

  const handleSetSentimentBulk = () => {
    setSentimentDialogOpen(true);
  };

  const handleConfirmBulkSentiment = async () => {
    await updateSentimentBulk(selectedArticles, bulkSentiment);
    setSentimentDialogOpen(false);
    setBulkSentiment('neutral');
  };

  const handleCloseBulkSentiment = () => {
    setSentimentDialogOpen(false);
    setBulkSentiment('neutral');
  };

  const handleAnalyzeSentimentBulk = async () => {
    if (!isSentimentAIEnabled()) {
      setSnackbar({ open: true, message: 'Ative sua MIA (Mensure IA) para usar a análise de sentimento.', severity: 'warning' });
      return;
    }
    await analyzeSentimentBulk(selectedArticles);
    setSentimentDialogOpen(false);
    setBulkSentiment('neutral');
  };

  const handleSetReadBulk = () => {
    toggleReadBulk(selectedArticles);
  };

  // Exclusão individual com confirmação
  const handleDeleteArticle = (articleId) => {
    setConfirmDialog({
      open: true,
      message: 'Tem certeza que deseja excluir este artigo?',
      onConfirm: async () => {
        const deleted = await deleteArticle(articleId);
        if (deleted) {
          removeArticlesFromCache(articleId);
        }
        setConfirmDialog(d => ({ ...d, open: false }));
      }
    });
  };

  // Exclusão em massa com confirmação e mensagem pluralizada
  const handleDeleteBulk = () => {
    const count = selectedArticles.length;
    if (count === 0) return;

    const confirmMsg = count === 1
      ? 'Tem certeza que deseja excluir o artigo selecionado?'
      : `Tem certeza que deseja excluir ${count} artigos selecionados?`;

    setConfirmDialog({
      open: true,
      message: confirmMsg,
      onConfirm: async () => {
        const deleted = await deleteArticlesBulk(selectedArticles);
        if (deleted) {
          removeArticlesFromCache(selectedArticles);
        }
        setConfirmDialog(d => ({ ...d, open: false }));
      }
    });
  };

  const handleNewArticle = () => {
    setScrapingModalOpen(true);
  };

  // Estado para loading de exportação
  const [exportingExcel, setExportingExcel] = React.useState(false);

  // Exportar artigos para Excel
  const handleExportExcel = async (tagMode = 'per_line') => {
    try {
      let fileName;

      if (selectedArticles.length > 0) {
        // Verificar se há IDs selecionados que não estão no array articles atual
        const loadedIds = articles.map(a => a._id);
        const missingIds = selectedArticles.filter(id => !loadedIds.includes(id));

        if (missingIds.length > 0) {
          // Há artigos selecionados que não estão carregados - buscar da API
          setExportingExcel(true);
          setSnackbar({
            open: true,
            message: `Buscando ${selectedArticles.length} artigos para exportação...`,
            severity: 'info'
          });

          try {
            // Buscar TODOS os artigos selecionados da API via POST (suporta muitos IDs)
            const response = await api.post('/articles/by-ids', {
              ids: selectedArticles
            });

            const allSelectedArticles = response.data.data || response.data.articles || response.data;

            if (!Array.isArray(allSelectedArticles) || allSelectedArticles.length === 0) {
              throw new Error('Não foi possível buscar os artigos selecionados');
            }

            fileName = exportArticlesToExcel(allSelectedArticles, 'artigos_selecionados', tagMode);
            setSnackbar({
              open: true,
              message: `${allSelectedArticles.length} artigos exportados para ${fileName}`,
              severity: 'success'
            });
          } finally {
            setExportingExcel(false);
          }
        } else {
          // Todos os artigos selecionados já estão carregados
          fileName = exportSelectedArticles(articles, selectedArticles, 'artigos_selecionados', tagMode);
          setSnackbar({
            open: true,
            message: `${selectedArticles.length} artigos exportados para ${fileName}`,
            severity: 'success'
          });
        }
      } else {
        // Exportar todos os carregados
        fileName = exportArticlesToExcel(articles, 'artigos', tagMode);
        setSnackbar({
          open: true,
          message: `${articles.length} artigos exportados para ${fileName}`,
          severity: 'success'
        });
      }
    } catch (error) {
      setExportingExcel(false);
      setSnackbar({
        open: true,
        message: error.message || 'Erro ao exportar artigos',
        severity: 'error'
      });
    }
  };

  // Enviar para Newsletter
  const handleSendNewsletter = async (articleOrIds) => {
    let ids = [];
    let singleArticle = null;
    let fallbackToFilter = false;
    
    if (Array.isArray(articleOrIds)) {
      // Massa (IDs)
      ids = articleOrIds;
    } else if (articleOrIds && articleOrIds._id) {
      // Individual (Objeto artigo)
      ids = [articleOrIds._id];
      singleArticle = articleOrIds;
    } else if (selectedArticles.length > 0) {
      // Fallback para seleção atual se nenhum parâmetro for passado
      ids = selectedArticles;
    } else {
      // NOVO: Se nada selecionado, verificar se há filtro de tag ativo
      // Podemos usar 'articles' (carregados) ou avisar o usuário
      if (articles.length > 0 && (tagId || filters.tags)) {
        ids = articles.map(a => a._id);
        fallbackToFilter = true;
      }
    }

    if (ids.length === 0) {
      setSnackbar({ open: true, message: 'Selecione ao menos um artigo para a newsletter', severity: 'warning' });
      return;
    }

    // Se veio de filtro e são vários, levar direto para o wizard com mensagem
    if (fallbackToFilter && ids.length > 0) {
       setSnackbar({ open: true, message: `Iniciando newsletter com ${ids.length} artigos da tag filtrada`, severity: 'info' });
       navigate('/newsletter', { 
         state: { 
           selectedArticleIds: ids,
           tagId: tagId || filters.tags 
         } 
       });
       return;
    }

    // Se for individual (ou apenas 1 selecionado), abrir modal rápido
    if (ids.length === 1) {
      let articleToUse = singleArticle;
      
      // Se não temos o objeto completo, precisamos buscar
      if (!articleToUse) {
        try {
          const res = await api.get(`/articles/${ids[0]}`);
          articleToUse = res.data.data || res.data;
        } catch (err) {
          console.error('Erro ao buscar artigo para modal rápido:', err);
        }
      }
      
      if (articleToUse) {
        setQuickNewsletterArticles([articleToUse]);
        setQuickNewsletterOpen(true);
        return;
      }
    }

    // Se for massa (>1), levar para a página completa (wizard)
    navigate('/newsletter', { 
      state: { 
        selectedArticleIds: ids,
        tagId: tagId || filters.tags
      } 
    });
  };

  // Handler para Corrigir Extração em Lote (SmartScraperIA)
  const handleBulkRescrapeSmart = () => {
    if (selectedArticles.length === 0) {
      setSnackbar({ open: true, message: 'Selecione pelo menos um artigo', severity: 'warning' });
      return;
    }

    if (selectedArticles.length > 30) {
      setSnackbar({ open: true, message: 'O limite do Smart Scraper em lote é 30 artigos por vez', severity: 'warning' });
      return;
    }

    // Usar o modal do sistema em vez do window.confirm nativo do navegador
    setConfirmDialog({
      open: true,
      title: '✨ Corrigir Extração com IA Premium',
      message: `Você tem certeza que deseja corrigir a extração de ${selectedArticles.length} artigo(s) usando IA Premium?\n\nIsso recarregará o conteúdo da fonte original de cada artigo selecionado.`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        try {
          setSmartScraperLoading(true);
          setSnackbar({
            open: true,
            message: `Iniciando re-extração com IA para ${selectedArticles.length} artigos...`,
            severity: 'info'
          });

          const response = await api.post('/articles/bulk-rescrape-smart', {
            articleIds: selectedArticles
          });

          setSnackbar({
            open: true,
            message: response.data.message || `Processamento concluído.`,
            severity: response.data.data.failed > 0 ? 'warning' : 'success'
          });

          refreshArticles();
          clearSelection();

        } catch (error) {
          setSnackbar({
            open: true,
            message: error.response?.data?.message || 'Erro ao processar em lote com Smart Scraper',
            severity: 'error'
          });
        } finally {
          setSmartScraperLoading(false);
        }
      }
    });
  };

  // Handler para Análise Qualitativa em Lote (KPIA)
  const handleBulkAnalyzeKpia = () => {
    if (selectedArticles.length === 0) {
      setSnackbar({ open: true, message: 'Selecione pelo menos um artigo', severity: 'warning' });
      return;
    }

    if (selectedArticles.length > 100) {
      setSnackbar({ open: true, message: 'O limite para Análise Qualitativa em lote é 100 artigos por vez', severity: 'warning' });
      return;
    }

    setConfirmDialog({
      open: true,
      title: '✨ Análise Qualitativa em Lote',
      message: `Você tem certeza que deseja gerar a Análise Qualitativa (KPIA) para os ${selectedArticles.length} artigo(s) selecionado(s)?\n\nIsso utilizará a IA para analisar ocupação, sentimento e exposição.`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        try {
          setAiBulkLoading(true);
          setSnackbar({
            open: true,
            message: `Iniciando Análise Qualitativa para ${selectedArticles.length} artigos...`,
            severity: 'info'
          });

          const response = await bulkAnalyzeKpia(selectedArticles).finally(() => setAiBulkLoading(false));

          setSnackbar({
            open: true,
            message: response.data?.message || `Processamento concluído.`,
            severity: response.data?.data?.failed > 0 ? 'warning' : 'success'
          });

          refreshArticles();
          clearSelection();

        } catch (error) {
          setSnackbar({
            open: true,
            message: error.response?.data?.message || 'Erro ao processar Análise Qualitativa em lote',
            severity: 'error'
          });
        }
      }
    });
  };

  // Handler para revisao nativa em lote (sem IA)
  const handleNativeReviewBulk = () => {
    if (selectedArticles.length === 0) {
      setSnackbar({ open: true, message: 'Selecione pelo menos um artigo', severity: 'warning' });
      return;
    }

    if (selectedArticles.length > 500) {
      setSnackbar({ open: true, message: 'O limite para revisao nativa em lote e 500 artigos por vez', severity: 'warning' });
      return;
    }

    setConfirmDialog({
      open: true,
      title: 'Revisao nativa em lote',
      message: `Revisar ${selectedArticles.length} artigo(s) selecionado(s) com regras nativas?\n\nIsso recalcula audiencia/valoracao por dominio, exposicao por briefing e area editorial sem usar IA.`,
      confirmText: 'Revisar',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        try {
          setSnackbar({
            open: true,
            message: `Iniciando revisao nativa para ${selectedArticles.length} artigos...`,
            severity: 'info'
          });

          const response = await bulkNativeReviewArticles(
            selectedArticles,
            ['audience', 'exposure', 'editorialOccupancy']
          );

          const result = response.data?.data;
          setSnackbar({
            open: true,
            message: response.data?.message || 'Revisao nativa concluida.',
            severity: result?.failed > 0 ? 'warning' : 'success'
          });

          refreshArticles();
          clearSelection();
        } catch (error) {
          setSnackbar({
            open: true,
            message: error.response?.data?.message || 'Erro ao executar revisao nativa em lote',
            severity: 'error'
          });
        }
      }
    });
  };

  // Handler universal para adicionar tags
  const handleAddTagsUniversal = async (articleId, newTags) => {
    // Buscar as tags atuais do backend
    const currentTags = await fetchCurrentArticleTags(articleId);
    const mergedTags = mergeTags(currentTags, newTags);
    const tagsToCreate = mergedTags.filter(tag => !tag._id);
    const existingTags = mergedTags.filter(tag => tag._id);
    let createdTags = [];

    if (tagsToCreate.length > 0) {
      const createPromises = tagsToCreate.map(tag =>
        api.post('/tags', { name: tag.name })
      );
      const createdResponses = await Promise.all(createPromises);
      createdTags = createdResponses.map(res => res.data.data);
    }

    const allTags = [...existingTags, ...createdTags];

    // Adicionar tags ao artigo principal
    await addTags(articleId, allTags);
    const updatedArticleRes = await api.get(`/articles/${articleId}`);
    setArticles(prev => prev.map(a => a._id === articleId ? updatedArticleRes.data.data : a));

    console.log('🏷️ [DEBUG] handleAddTagsUniversal - allTags:', JSON.stringify(allTags, null, 2));

    // Atualizar lista de tags se novas tags foram criadas
    if (createdTags.length > 0) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      refreshTags();
    }

    setTagDialogOpen(false);
    setArticleForTags(null);

    // === PROPAGAÇÃO DE TAGS PARA SIMILARES ===
    const preference = getTagPropagationPreference();

    // Se preferência é 'never', não propagar
    if (preference === 'never') {
      return;
    }

    // Buscar artigos similares
    try {
      const similarRes = await api.post(`/articles/${articleId}/similar`, { minScore: 90 });
      const similares = similarRes.data?.similarArticles || [];

      // Se não há similares, termina
      if (similares.length === 0) {
        return;
      }

      // Se preferência é 'always', propagar automaticamente
      if (preference === 'always') {
        // Propagar cada tag para os similares
        for (const tag of allTags) {
          const similarIds = similares.map(a => a._id);
          await api.post('/articles/bulk-add-tag', {
            articleIds: similarIds,
            tagId: tag._id
          });
        }

        // Atualizar estado local dos similares
        const similarIds = similares.map(a => a._id);
        setArticles(prev => prev.map(a => {
          if (similarIds.includes(a._id)) {
            const existingTags = a.tags || [];
            // Criar mapa de tags existentes (normalizando para objetos)
            const tagMap = new Map();
            existingTags.forEach(t => {
              const id = typeof t === 'object' ? t._id : t;
              let tagObj = typeof t === 'object' ? t : { _id: t };

              // Tentar hidratar o nome da tag se estiver faltando
              if (!tagObj.name && tags && Array.isArray(tags)) {
                const found = tags.find(gt => gt._id === id);
                if (found) {
                  tagObj = { ...found }; // Copiar para evitar mutação
                } else {
                  tagObj.name = '...'; // Fallback visual
                }
              } else if (!tagObj.name) {
                tagObj.name = '...';
              }

              tagMap.set(id, tagObj);
            });

            // Adicionar novas tags (garantindo que são objetos)
            allTags.forEach(t => {
              if (typeof t === 'object' && t._id) {
                tagMap.set(t._id, t);
              }
            });

            return { ...a, tags: Array.from(tagMap.values()) };
          }
          return a;
        }));

        setSnackbar({
          open: true,
          message: `Tags aplicadas a ${similares.length + 1} artigos`,
          severity: 'success'
        });
        return;
      }

      // Se preferência é 'ask', abrir dialog
      setPendingArticleIdForTags(articleId);
      setPendingTagsToPropagate(allTags);
      setSimilarArticlesForTags(similares);
      setTagPropagationDialogOpen(true);

    } catch (error) {
      console.error('Erro ao buscar artigos similares:', error);
    }
  };

  // Handler para Smart Scraper (IA Premium)
  const handleRescrapeSmart = async (article) => {
    try {
      setSnackbar({
        open: true,
        message: 'Corrigindo artigo com IA Premium... Isso pode levar até 1 minuto.',
        severity: 'info'
      });

      const { rescrapeSmartArticle } = require('../../../services/api');
      const response = await rescrapeSmartArticle(article._id);

      if (response.data?.success && response.data?.data) {
        setArticles(prev => prev.map(a => 
          a._id === article._id ? response.data.data : a
        ));
        
        setSnackbar({
          open: true,
          message: 'Extração corrigida com sucesso!',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Erro ao corrigir artigo com IA:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Erro ao corrigir o artigo com IA Premium',
        severity: 'error'
      });
    }
  };

  // Handler para confirmar propagação de tags
  const handleTagPropagationConfirm = async (savePreference) => {
    setTagPropagationLoading(true);

    // Salvar preferência se solicitado
    if (savePreference === 'always') {
      setTagPropagationPreference('always');
    }

    try {
      // Propagar cada tag para os similares
      for (const tag of pendingTagsToPropagate) {
        const similarIds = similarArticlesForTags.map(a => a._id);
        await api.post('/articles/bulk-add-tag', {
          articleIds: similarIds,
          tagId: tag._id
        });
      }

      // Atualizar estado local dos similares
      console.log('🏷️ [DEBUG] handleTagPropagationConfirm - pendingTags:', JSON.stringify(pendingTagsToPropagate, null, 2));
      const similarIds = similarArticlesForTags.map(a => a._id);
      setArticles(prev => prev.map(a => {
        if (similarIds.includes(a._id)) {
          console.log('🔄 Atualizando tags para artigo similar:', a.title, 'Tags atuais:', a.tags);
          const existingTags = a.tags || [];
          // Criar mapa de tags existentes
          const tagMap = new Map();
          existingTags.forEach(t => {
            const id = typeof t === 'object' ? t._id : t;
            let tagObj = typeof t === 'object' ? t : { _id: t };

            // Tentar hidratar o nome da tag se estiver faltando
            if (!tagObj.name && tags && Array.isArray(tags)) {
              const found = tags.find(gt => gt._id === id);
              if (found) {
                tagObj = { ...found }; // Copiar para evitar mutação
              } else {
                tagObj.name = '...'; // Fallback visual
              }
            } else if (!tagObj.name) {
              tagObj.name = '...';
            }

            tagMap.set(id, tagObj);
          });

          // Adicionar novas tags
          pendingTagsToPropagate.forEach(t => {
            if (typeof t === 'object' && t._id) {
              tagMap.set(t._id, t);
            }
          });

          const newTagsList = Array.from(tagMap.values());
          console.log('✅ Novas tags para o artigo:', newTagsList);
          return { ...a, tags: newTagsList };
        }
        return a;
      }));

      setSnackbar({
        open: true,
        message: `Tags aplicadas a ${similarArticlesForTags.length + 1} artigos`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro ao propagar tags:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao propagar tags',
        severity: 'error'
      });
    }

    // Limpar estado
    setTagPropagationDialogOpen(false);
    setTagPropagationLoading(false);
    setPendingTagsToPropagate([]);
    setPendingArticleIdForTags(null);
    setSimilarArticlesForTags([]);
  };

  // Handler para cancelar propagação de tags (apenas este artigo)
  const handleTagPropagationCancel = (savePreference) => {
    // Salvar preferência se solicitado
    if (savePreference === 'never') {
      setTagPropagationPreference('never');
    }

    // Limpar estado
    setTagPropagationDialogOpen(false);
    setPendingTagsToPropagate([]);
    setPendingArticleIdForTags(null);
    setSimilarArticlesForTags([]);
  };

  // Handler para fechar dialog de propagação de tags
  const handleTagPropagationClose = () => {
    setTagPropagationDialogOpen(false);
    setPendingTagsToPropagate([]);
    setPendingArticleIdForTags(null);
    setSimilarArticlesForTags([]);
  };

  return (
    <>
      <ArticlePageTemplate
        title={displayTitle}
        subtitle={pageSubtitle}
        iconType={tagId ? 'tag' : collectionId ? 'collection' : undefined}
        onNewArticle={pageConfig.showScraping !== false ? handleNewArticle : undefined}

        // MainToolbar props
        searchValue={searchQuery}
        onSearch={handleSearch}
        selectionMode={selectionMode}
        onToggleSelectionMode={toggleSelectionMode}
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen(prev => !prev)}
        sortBy={sortBy}
        onSortChange={handleSortChange}
        sortOptions={sortOptions}
        cardsPerPage={parseInt(localStorage.getItem('article_cardsPerPage')) || 10}
        onCardsPerPageChange={handleCardsPerPageChange}
        variant={variant}
        onVariantChange={handleVariantChange}

        // Estatísticas para MainToolbar
        totalItems={articles.length} // Total de artigos carregados do backend
        filteredItems={filteredArticles.length} // Artigos após filtros locais
        selectedItems={selectedArticles.length} // Artigos selecionados

        // Props para filtro de período
        timePeriod={timePeriod}
        quickDateType={quickDateType}
        onQuickDateTypeChange={(newType) => {
          // Salvar preferência
          setQuickDateType(newType);
          localStorage.setItem('article_quickDateType', newType);
          // Conexão direta ao handleFilterChange → invalida cache React Query via setFilterKey
          // Preserva outros filtros ativos e atualiza apenas o dateType
          if (timePeriod && timePeriod !== 'all' && timePeriod !== 'custom') {
            // Para períodos pré-definidos: atualizar dateType e limpar datas manuais
            handleFilterChange({ ...filters, startDate: null, endDate: null, date: '', dateType: newType });
          } else if (timePeriod === 'custom' && filters.startDate && filters.endDate) {
            // No período personalizado, trocar o seletor superior também deve
            // trocar o campo usado pela consulta, preservando o intervalo.
            handleFilterChange({ ...filters, dateType: newType });
          }
        }}
        onTimePeriodChange={(newPeriod, customDates) => {
          if (newPeriod === 'custom' && customDates) {
            const appliedDateType = customDates.dateType || 'published';
            // Período personalizado: aplicar datas
            setTimePeriod('custom');
            localStorage.setItem('article_timePeriod', 'custom');
            // O seletor rápido deve representar o mesmo tipo de data que está
            // efetivamente aplicado no período personalizado.
            setQuickDateType(appliedDateType);
            localStorage.setItem('article_quickDateType', appliedDateType);
            // Aplicar datas E o tipo de data escolhido pelo usuário (publicação vs cadastro)
            handleFilterChange({ ...filters, startDate: customDates.startDate, endDate: customDates.endDate, date: '', dateType: appliedDateType });
          } else {
            // Período predefinido: limpar datas e aplicar timePeriod + dateType atual do toggle
            setTimePeriod(newPeriod);
            localStorage.setItem('article_timePeriod', newPeriod);
            // Limpar TODOS os filtros de data dos AdvancedFilters para evitar conflito
            handleFilterChange({ ...filters, startDate: null, endDate: null, date: '', dateType: quickDateType || null });
          }
        }}
        totalCount={totalCount}
        customDateRange={
          (filters.startDate || filters.endDate)
            ? { startDate: filters.startDate, endDate: filters.endDate, dateType: filters.dateType || 'published' }
            : null
        }

        // QuickFilters - Filtro de favoritos
        showFavoritesOnly={showFavoritesOnly}
        onToggleFavorites={handleToggleFavorites}

        // AdvancedFilters props
        dateValue={filters.date}
        onDateChange={(value, customRange) => {
          let dateFilter = {};

          if (value === 'last_day') {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            dateFilter = {
              date: 'last_day',
              startDate: yesterday
            };
          } else if (value === 'last_week') {
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            dateFilter = {
              date: 'last_week',
              startDate: weekAgo
            };
          } else if (value === 'last_month') {
            const monthAgo = new Date();
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            dateFilter = {
              date: 'last_month',
              startDate: monthAgo
            };
          } else if (value === 'custom' && customRange) {
            const appliedDateType = customRange.dateType || 'published';
            dateFilter = {
              date: 'custom',
              startDate: customRange.startDate,
              endDate: customRange.endDate,
              dateType: appliedDateType
            };
            setQuickDateType(appliedDateType);
            localStorage.setItem('article_quickDateType', appliedDateType);
          } else if (value === 'custom') {
            // Manter o filtro custom mas sem datas específicas ainda
            dateFilter = { date: 'custom' };
          } else {
            // Limpar filtros de data
            dateFilter = {
              date: '',
              startDate: null,
              endDate: null,
              dateType: null
            };
          }

          // Resetar o timePeriod para 'all' para evitar dois filtros de data conflitantes no backend
          // (timePeriod filtra createdAt; startDate/endDate filtram 'date' ou 'createdAt')
          if (value && value !== '') {
            setTimePeriod('all');
            localStorage.setItem('article_timePeriod', 'all');
          }

          handleFilterChange({ ...filters, ...dateFilter });
        }}
        dateOptions={dateOptions}
        selectedSources={selectedSources}
        onSourcesChange={(sources) => {
          console.log('📰 Fontes selecionadas:', sources);
          setSelectedSources(sources);
          const sourcesFilter = sources.join(',');
          console.log('📤 Enviando filtro de fontes para API:', sourcesFilter);
          handleFilterChange({ ...filters, siteName: sourcesFilter });
        }}
        sourceOptions={[...allSourceOptions].sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' }))}
        selectedSentiments={selectedSentiments}
        onSentimentsChange={(sentiments) => {
          setSelectedSentiments(sentiments);
          handleFilterChange({ ...filters, sentiment: sentiments.join(',') });
        }}
        sentimentOptions={sentimentOptions}
        selectedExposures={selectedExposures}
        onExposuresChange={(exposures) => {
          setSelectedExposures(exposures);
          handleFilterChange({ ...filters, exposure: exposures.join(',') });
        }}
        exposureOptions={exposureOptions}
        selectedTags={selectedTags}
        onTagsChange={(tags) => {
          console.log('🏷️ Tags selecionadas:', tags);
          setSelectedTags(tags);
          const tagsFilter = tags.join(',');
          console.log('📤 Enviando filtro de tags para API:', tagsFilter);
          handleFilterChange({ ...filters, tags: tagsFilter });
        }}
        tagOptions={tags.map(tag => ({
          value: tag._id,
          label: tag.name
        })).sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' }))}
        selectedCollections={selectedCollections}
        onCollectionsChange={(collections) => {
          setSelectedCollections(collections);
          handleFilterChange({ ...filters, collections: collections.join(',') });
        }}
        collectionOptions={collectionOptions}
        selectedMediaTypes={selectedMediaTypes}
        onMediaTypesChange={(mediaTypes) => {
          setSelectedMediaTypes(mediaTypes);
          handleFilterChange({ ...filters, mediaType: mediaTypes.join(',') });
        }}
        mediaTypeOptions={mediaTypeOptions}
        selectedContentTypes={selectedContentTypes}
        onContentTypesChange={(contentTypes) => {
          setSelectedContentTypes(contentTypes);
          handleFilterChange({ ...filters, contentType: contentTypes.join(',') });
        }}
        contentTypeOptions={contentTypeOptions}
        selectedTiers={selectedTiers}
        onTiersChange={(tiers) => {
          setSelectedTiers(tiers);
          handleFilterChange({ ...filters, tier: tiers.join(',') });
        }}
        tierOptions={tierOptions}
        selectedStatuses={selectedStatuses}
        onStatusesChange={handleStatusesChange}
        statusOptions={statusOptions}
        selectedEntityType={selectedEntityType}
        onEntityTypeChange={(val) => {
          setSelectedEntityType(val);
          handleFilterChange({ ...filters, entityType: val });
        }}
        entityTypeOptions={entityTypeOptions}
        showTagFilter={true}
        showCollectionFilter={true}
        onClearFilters={handleClearFilters}

        // ActiveFiltersIndicator props
        activeFilters={activeFilters}
        onRemoveFilter={handleRemoveFilter}
        activeFiltersExpanded={activeFiltersExpanded}
        onToggleActiveFiltersExpanded={() => setActiveFiltersExpanded(prev => !prev)}

        // SelectionToolbar props
        onSelectAll={selectAll}
        onEditFields={handleEditFieldsBulk}
        onNativeReview={handleNativeReviewBulk}
        onFavorite={handleFavorite}
        onAddTag={handleAddTagBulk}
        onAddToCollection={handleAddToCollectionBulk}
        onSetSentiment={handleSetSentimentBulk}
        onSetRead={handleSetReadBulk}
        onDelete={handleDeleteBulk}
        onSendNewsletter={handleSendNewsletter}
        onBulkRescrapeSmart={handleBulkRescrapeSmart}
        onBulkAnalyzeKpia={handleBulkAnalyzeKpia}
        onClearSelection={clearSelection}
        onExportExcel={handleExportExcel}
        selectedCount={selectedArticles.length}
        allSelected={selectedArticles.length >= totalCount && totalCount > 0}
        showRemoveFromTag={false}
        showRemoveFromCollection={false}
        onRemoveTags={handleRemoveTagsBulk}
        onRemoveFromCollections={handleRemoveFromCollectionsBulk}
        // Props para seleção estilo Gmail
        currentPageCount={articles.length}
        onSelectAllPages={handleSelectAllPages}
        selectingAllPages={selectingAll}
        bulkActionLoading={bulkActionLoading || smartScraperLoading || aiBulkLoading}
        bulkActionProgress={bulkActionProgress}
        exportingExcel={exportingExcel}

        // Estado
        loading={loading}
        error={error}
      >
        <ArticleListPageAlerts
          pendingPrintsCount={pendingPrintsCount}
          isMobile={isMobile}
          hasNewArticles={hasNewArticles}
          theme={theme}
          onOpenPendingPrints={() => setPendingPrintsModalOpen(true)}
          onRefreshArticles={refreshCacheAndData}
        />

        {/* O modo mensuracao usa uma tabela direta, sem passar pela lista/card. */}
        {loading || isRestoringSession || isMounting || filteredArticles.length > 0 ? (
          variant === 'measurement' ? (
            <Box sx={{ mt: 2.5 }}>
              <ArticleMeasurementTable
                articles={isMounting ? [] : filteredArticles}
                loading={loading || isRestoringSession || isMounting}
                onArticleClick={handleArticleClick}
                onEdit={onEdit}
                onMeasurementSave={handleMeasurementSave}
                onMeasurementBulkSave={handleMeasurementBulkSave}
                selectionMode={selectionMode}
                selectedArticles={selectedArticles}
                onSelectArticle={toggleItemSelection}
              />

              {hasMore && (
                <Divider
                  sx={{
                    my: 3,
                    borderColor: 'rgba(255,255,255,0.12)',
                    '&::before, &::after': { borderColor: 'rgba(255,255,255,0.12)' }
                  }}
                  textAlign="center"
                >
                  <Button
                    variant="outlined"
                    onClick={loadMore}
                    disabled={loading}
                    sx={{
                      px: 8,
                      py: 0.65,
                      borderRadius: 999,
                      fontWeight: 700,
                      textTransform: 'none',
                      borderColor: 'rgba(255,255,255,0.16)'
                    }}
                  >
                    {loading ? 'Carregando...' : 'Ver mais'}
                  </Button>
                </Divider>
              )}
            </Box>
          ) : (
            <React.Suspense fallback={null}>
              <ArticleList
                articles={isMounting ? [] : filteredArticles}
                loading={loading || isRestoringSession || isMounting}
                error={error}
                onArticleClick={handleArticleClick}
                onToggleFavorite={customToggleFavorite}
                onToggleRead={handleToggleRead}
                onUpdateSentiment={updateSentimentWithConfirmation}
                variant={variant}
                onEdit={onEdit}
                selectionMode={selectionMode}
                selectedArticles={selectedArticles}
                onSelectArticle={toggleItemSelection}
                onSelectAll={selectAll}
                onDeselectAll={clearSelection}
                onAddTags={handleAddTagsUniversal}
                onRemoveTag={handleArticleRemoveTag}
                onRescrapeSmart={handleRescrapeSmart}
                availableTags={availableTags}
                onDelete={handleDeleteArticle}
                onSendNewsletter={handleSendNewsletter}
                onAddToCollection={handleAddToCollectionIndividual}
                hasMore={hasMore}
                loadMore={loadMore}
              />
            </React.Suspense>
          )
        ) : showEmpty ? (
          <ArticleListEmptyState
            hasActiveFilters={hasEmptyStateFilters}
            onClearFilters={handleClearFilters}
          />
        ) : null}
      </ArticlePageTemplate>

      <ArticleListDialogs
        selectedArticle={selectedArticle}
        selectedIndex={selectedIndex}
        articlesCount={articles.length}
        modalOpen={modalOpen}
        onCloseModal={handleCloseModal}
        onArticleUpdated={handleArticleUpdated}
        onToggleFavorite={customToggleFavorite}
        onToggleReadArticle={handleToggleReadArticle}
        onUpdateSentiment={updateSentimentWithConfirmation}
        onPrevArticle={handlePrevArticle}
        onNextArticle={handleNextArticle}
        onAddTags={handleAddTagsUniversal}
        onRemoveArticleTag={handleArticleRemoveTag}
        onEdit={onEdit}
        onDeleteArticle={handleDeleteArticle}
        onSendNewsletter={handleSendNewsletter}
        availableTags={availableTags}
        editModalOpen={editModalOpen}
        onCloseEditModal={handleCloseEditModal}
        onSaveArticle={handleSaveArticle}
        onSelectArticle={setSelectedArticle}
        onRemoveTag={removeTag}
        tagDialogOpen={tagDialogOpen}
        onCloseTagDialog={() => setTagDialogOpen(false)}
        articleForTags={articleForTags}
        bulkTagDialogOpen={bulkTagDialogOpen}
        onCloseBulkTagDialog={() => setBulkTagDialogOpen(false)}
        onBulkAddTags={handleBulkAddTags}
        bulkEditDialogOpen={bulkEditDialogOpen}
        onCloseBulkEditDialog={() => setBulkEditDialogOpen(false)}
        selectedArticles={selectedArticles}
        bulkActionLoading={bulkActionLoading}
        onApplyBulkFieldEdit={handleApplyBulkFieldEdit}
        collectionDialogOpen={collectionDialogOpen}
        onCloseCollectionDialog={() => {
          setCollectionDialogOpen(false);
          setArticlesToAddToCollection([]);
        }}
        articlesToAddToCollection={articlesToAddToCollection}
        onCollectionSuccess={handleCollectionSuccess}
        removeTagsDialogOpen={removeTagsDialogOpen}
        onCloseRemoveTagsDialog={() => setRemoveTagsDialogOpen(false)}
        onRemoveTagsSuccess={handleRemoveTagsSuccess}
        removeFromCollectionsDialogOpen={removeFromCollectionsDialogOpen}
        onCloseRemoveFromCollectionsDialog={() => setRemoveFromCollectionsDialogOpen(false)}
        onRemoveFromCollectionsSuccess={handleRemoveFromCollectionsSuccess}
        sentimentDialogOpen={sentimentDialogOpen}
        onCloseBulkSentiment={handleCloseBulkSentiment}
        bulkSentiment={bulkSentiment}
        onBulkSentimentChange={setBulkSentiment}
        onConfirmBulkSentiment={handleConfirmBulkSentiment}
        onAnalyzeSentimentBulk={handleAnalyzeSentimentBulk}
        aiSentimentEnabled={isSentimentAIEnabled()}
        showBackgroundScraping={pageConfig.showBackgroundScraping !== false}
        scrapingModalOpen={scrapingModalOpen}
        onCloseScrapingModal={() => setScrapingModalOpen(false)}
        onRestoreScrapingModal={() => setScrapingModalOpen(true)}
        onBackgroundScrapingJobStarted={handleBackgroundScrapingJobStarted}
        pendingPrintsModalOpen={pendingPrintsModalOpen}
        onClosePendingPrintsModal={() => setPendingPrintsModalOpen(false)}
        onPrintsUpdated={setPendingPrintsCount}
        confirmDialog={confirmDialog}
        onCancelConfirmDialog={() => setConfirmDialog(d => ({ ...d, open: false }))}
        propagationDialogOpen={propagationDialogOpen}
        onPropagationClose={handlePropagationClose}
        onPropagationConfirm={handlePropagationConfirm}
        onPropagationCancel={handlePropagationCancel}
        pendingSentiment={pendingSentiment}
        similarArticles={similarArticles}
        propagationDialogLoading={propagationDialogLoading}
        tagPropagationDialogOpen={tagPropagationDialogOpen}
        onTagPropagationClose={handleTagPropagationClose}
        onTagPropagationConfirm={handleTagPropagationConfirm}
        onTagPropagationCancel={handleTagPropagationCancel}
        pendingTagsToPropagate={pendingTagsToPropagate}
        similarArticlesForTags={similarArticlesForTags}
        tagPropagationLoading={tagPropagationLoading}
        snackbar={snackbar}
        onCloseSnackbar={handleCloseSnackbar}
        quickNewsletterOpen={quickNewsletterOpen}
        onCloseQuickNewsletter={() => setQuickNewsletterOpen(false)}
        quickNewsletterArticles={quickNewsletterArticles}
        onQuickNewsletterEmailSent={handleQuickNewsletterEmailSent}
      />
    </>
  );
};

/**
 * PropTypes do ArticleListPageTemplate
 */
ArticleListPageTemplate.propTypes = {
  // Filtro base para a busca de artigos
  baseFilter: PropTypes.object,

  // Configuração da página
  pageTitle: PropTypes.string,
  pageSubtitle: PropTypes.string,

  // Configurações opcionais
  pageConfig: PropTypes.shape({
    showScraping: PropTypes.bool,
    showBackgroundScraping: PropTypes.bool,
    showTagHeader: PropTypes.bool,
    showCollectionHeader: PropTypes.bool
  })
};

ArticleListPageTemplate.defaultProps = {
  baseFilter: {},
  pageTitle: 'Artigos',
  pageSubtitle: '',
  pageConfig: {
    showScraping: true,
    showBackgroundScraping: true,
    showTagHeader: false,
    showCollectionHeader: false
  }
};

export default ArticleListPageTemplate;
